import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import BuilderStartScreen from './BuilderStartScreen';
import BuilderPromptScreen from './BuilderPromptScreen';
import BuilderWorkspace from './BuilderWorkspace';
import BuilderSidebar from './BuilderSidebar';
import BuilderProjectsGrid from './BuilderProjectsGrid';
import SessionBootScreen from './SessionBootScreen';
import { useProjectStore } from '../../stores/projectStore';
import { useSession } from '../../context/SessionContext';
import { cloudProjectApi } from '../../services/cloudApi';
import { getAuthToken } from '../../utils/auth';

// Builder flow states
const BUILDER_STATES = {
  START: 'start',
  PROJECTS: 'projects',
  PROMPT: 'prompt',
  WORKSPACE: 'workspace',
  BOOTING: 'booting',
};

function BuilderPage({ onStateChange, chatSidebarOpen = true, viewMode = 'preview', deviceMode = 'desktop', initialProjectId, onProjectChange }) {
  // Navigation
  const navigate = useNavigate();
  const location = useLocation();

  // Session context (cloud mode)
  const { isReady: sessionReady, sessionStatus, error: sessionError, retrySession, isCloudMode } = useSession();

  // Project store
  const {
    currentProject,
    projects,
    loadProjects,
    createProject,
    setCurrentProject,
    clearMessages,
    stopAllVite,
  } = useProjectStore();

  const [builderState, setBuilderState] = useState(null); // null = loading
  const [hasInitialized, setHasInitialized] = useState(false);
  const [hasHandledInitialProject, setHasHandledInitialProject] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [activeChat, setActiveChat] = useState(null);

  // Pending action queued while session boots (cloud mode only)
  const [pendingAction, setPendingAction] = useState(null);
  const [localError, setLocalError] = useState(null);
  const pendingActionRef = useRef(null);
  pendingActionRef.current = pendingAction;

  // Load projects on mount and set initial state
  useEffect(() => {
    const initializeProjects = async () => {
      await loadProjects();
      setHasInitialized(true);
    };
    initializeProjects();
  }, []);

  // Handle initial project ID from URL (e.g., /experience/:projectId)
  useEffect(() => {
    if (hasInitialized && initialProjectId && !hasHandledInitialProject) {
      setHasHandledInitialProject(true);

      if (isCloudMode && !sessionReady) {
        // Session not ready yet — queue the open action and show boot screen
        setPendingAction({ type: 'open', projectId: initialProjectId });
        setBuilderState(BUILDER_STATES.BOOTING);
        return;
      }

      const loadInitialProject = async () => {
        try {
          const loadedProject = await setCurrentProject(initialProjectId);
          const project = loadedProject || useProjectStore.getState().currentProject;
          setSelectedExperience({
            id: project?.aws_project_id || initialProjectId,
            name: project?.display_name || project?.name || 'Project',
            projectId: project?.aws_project_id || initialProjectId,
          });
          setCurrentPrompt('');
          setBuilderState(BUILDER_STATES.WORKSPACE);
        } catch (error) {
          console.error('Failed to load project from URL:', error);
          setBuilderState(projects.length > 0 ? BUILDER_STATES.PROJECTS : BUILDER_STATES.START);
        }
      };
      loadInitialProject();
    }
  }, [hasInitialized, initialProjectId, hasHandledInitialProject, projects, setCurrentProject, isCloudMode, sessionReady]);

  // Once projects are loaded, set the appropriate initial state (only if no initial project to load)
  useEffect(() => {
    if (hasInitialized && !initialProjectId) {
      if (builderState === null) {
        setBuilderState(projects.length > 0 ? BUILDER_STATES.PROJECTS : BUILDER_STATES.START);
      }
    }
  }, [projects, hasInitialized, builderState, initialProjectId]);

  // Reset state when navigating from /experience/:id to /dashboard
  useEffect(() => {
    if (hasInitialized && !initialProjectId && location.pathname === '/dashboard') {
      if (builderState === BUILDER_STATES.WORKSPACE && selectedExperience) {
        stopAllVite();
        setSelectedExperience(null);
        setCurrentPrompt('');
        setActiveChat(null);
        setPendingAction(null);
        setBuilderState(projects.length > 0 ? BUILDER_STATES.PROJECTS : BUILDER_STATES.START);
        setHasHandledInitialProject(false);
      }
    }
  }, [location.pathname, initialProjectId, hasInitialized, builderState, selectedExperience, projects.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // Boot completion: session became ready while we have a pending action
  // ============================================================
  useEffect(() => {
    if (!sessionReady || builderState !== BUILDER_STATES.BOOTING || !pendingActionRef.current) return;

    const action = pendingActionRef.current;
    const completeBoot = async () => {
      try {
        if (action.type === 'new') {
          // AWS project already created — now clone to session backend
          const awsProjectId = action.awsProject.id;
          const authToken = getAuthToken();
          const { projectApi } = await import('../../services/api');
          const cloneResult = await projectApi.cloneFromAws(awsProjectId, authToken);
          const project = {
            ...cloneResult.project,
            aws_project_id: awsProjectId,
            awsProject: action.awsProject,
          };

          // Update the store
          useProjectStore.setState(state => ({
            projects: [...state.projects, project],
            currentProject: project,
            isLoading: false,
          }));

          // Load files and start Vite
          await useProjectStore.getState().loadFiles();
          await useProjectStore.getState().startVite().catch(err => {
            console.error('Auto-start Vite failed:', err);
          });

          setSelectedExperience({
            id: awsProjectId,
            name: action.awsProject.name || project.name,
            projectId: awsProjectId,
            solutionId: action.solutionId || null,
          });

          if (action.prompt) {
            setCurrentPrompt(action.prompt);
            setBuilderState(BUILDER_STATES.WORKSPACE);
          } else {
            setBuilderState(BUILDER_STATES.PROMPT);
          }
          navigate(`/experience/${awsProjectId}`, { replace: true });

        } else if (action.type === 'open') {
          const loadedProject = await setCurrentProject(action.projectId);
          const project = loadedProject || useProjectStore.getState().currentProject;
          setSelectedExperience({
            id: project?.aws_project_id || action.projectId,
            name: project?.display_name || project?.name || 'Project',
            projectId: project?.aws_project_id || action.projectId,
          });
          setCurrentPrompt('');
          setBuilderState(BUILDER_STATES.WORKSPACE);
          navigate(`/experience/${project?.aws_project_id || action.projectId}`, { replace: true });
        }
      } catch (error) {
        console.error('Failed to complete boot action:', error);
        setBuilderState(projects.length > 0 ? BUILDER_STATES.PROJECTS : BUILDER_STATES.START);
      } finally {
        setPendingAction(null);
      }
    };

    completeBoot();
  }, [sessionReady, builderState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent of state changes
  useEffect(() => {
    if (onStateChange) {
      onStateChange(builderState);
    }
  }, [builderState, onStateChange]);

  // Notify parent of project changes
  useEffect(() => {
    if (onProjectChange) {
      onProjectChange(currentProject || selectedExperience);
    }
  }, [currentProject, selectedExperience, onProjectChange]);

  /**
   * Handle project selection (from existing projects)
   */
  const handleSelectExperience = async (experience) => {
    if (experience.projectId) {
      if (isCloudMode && !sessionReady) {
        setPendingAction({ type: 'open', projectId: experience.projectId });
        setBuilderState(BUILDER_STATES.BOOTING);
        return;
      }
      await setCurrentProject(experience.projectId);
      setSelectedExperience(experience);
      setBuilderState(BUILDER_STATES.WORKSPACE);
      navigate(`/experience/${experience.projectId}`, { replace: true });
    }
  };

  /**
   * Handle creating new project from template.
   * In cloud mode: if session not ready, create on AWS immediately (non-blocking)
   * and show boot screen. The clone happens when session is ready.
   */
  const handleSelectTemplate = async (template = 'vite-react', options = {}) => {
    try {
      const { solutionId } = options;
      const projectName = solutionId
        ? `Workspace ${solutionId}`
        : 'New Project';
      const description = solutionId
        ? `Threedium workspace for solution ${solutionId}`
        : 'Created from Builder';

      if (isCloudMode && !sessionReady) {
        // Non-blocking: create on AWS (doesn't need session backend), then queue
        const awsProject = await cloudProjectApi.createFromTemplate({
          name: projectName,
          template,
          description,
          solution_id: solutionId,
        });
        setPendingAction({
          type: 'new',
          awsProject,
          template,
          solutionId: solutionId || null,
          projectCreated: true,
        });
        setBuilderState(BUILDER_STATES.BOOTING);
        navigate(`/experience/${awsProject.id}`, { replace: true });
        return;
      }

      // Fast path: session already running
      const project = await createProject(projectName, template, description, solutionId);
      setSelectedExperience({
        id: project.aws_project_id || project.id,
        name: project.name,
        projectId: project.aws_project_id || project.id,
        solutionId: solutionId || null,
      });
      setBuilderState(BUILDER_STATES.PROMPT);
      navigate(`/experience/${project.aws_project_id || project.id}`, { replace: true });
    } catch (error) {
      console.error('Failed to create project:', error);
      setLocalError(error.message || 'Failed to create project. Please try again.');
      setBuilderState(BUILDER_STATES.BOOTING);
    }
  };

  /**
   * Handle prompt submission - create project if needed
   */
  const handleSubmitPrompt = async (prompt) => {
    if (!currentProject) {
      try {
        const projectName = prompt.substring(0, 30).replace(/[^a-zA-Z0-9 ]/g, '') || 'New Project';

        if (isCloudMode && !sessionReady) {
          const awsProject = await cloudProjectApi.createFromTemplate({
            name: projectName,
            template: 'vite-react',
            description: prompt.substring(0, 100),
          });
          setPendingAction({
            type: 'new',
            awsProject,
            template: 'vite-react',
            projectCreated: true,
            prompt,
          });
          setBuilderState(BUILDER_STATES.BOOTING);
          navigate(`/experience/${awsProject.id}`, { replace: true });
          return;
        }

        const project = await createProject(projectName, 'vite-react', prompt.substring(0, 100));
        setSelectedExperience({
          id: project.aws_project_id || project.id,
          name: project.name,
          projectId: project.aws_project_id || project.id,
        });
        navigate(`/experience/${project.aws_project_id || project.id}`, { replace: true });
      } catch (error) {
        console.error('Failed to create project:', error);
        setLocalError(error.message || 'Failed to create project. Please try again.');
        setBuilderState(BUILDER_STATES.BOOTING);
        return;
      }
    }

    setCurrentPrompt(prompt);
    setBuilderState(BUILDER_STATES.WORKSPACE);
  };

  /**
   * Handle new chat / reset
   */
  const handleNewChat = () => {
    stopAllVite();
    setBuilderState(BUILDER_STATES.START);
    setSelectedExperience(null);
    setCurrentPrompt('');
    setActiveChat(null);
    setPendingAction(null);
    setLocalError(null);
    clearMessages();
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard', { replace: true });
    }
  };

  /**
   * Handle going back to projects list
   */
  const handleBackToProjects = () => {
    stopAllVite();
    if (projects.length > 0) {
      setBuilderState(BUILDER_STATES.PROJECTS);
    } else {
      setBuilderState(BUILDER_STATES.START);
    }
    setSelectedExperience(null);
    setCurrentPrompt('');
    setPendingAction(null);
    setLocalError(null);
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard', { replace: true });
    }
    setActiveChat(null);
  };

  /**
   * Handle selecting existing chat/project from sidebar
   */
  const handleSelectChat = async (chat) => {
    setActiveChat(chat);

    const projectId = chat.projectId || chat.id;
    if (projectId) {
      if (isCloudMode && !sessionReady) {
        setPendingAction({ type: 'open', projectId });
        setBuilderState(BUILDER_STATES.BOOTING);
        return;
      }
      await setCurrentProject(projectId);
      setSelectedExperience({
        id: projectId,
        name: chat.name || chat.title,
        projectId: projectId,
      });
    } else {
      setSelectedExperience({ id: chat.experienceId, name: chat.title });
    }

    setCurrentPrompt('');
    setBuilderState(BUILDER_STATES.WORKSPACE);
  };

  const handleBootRetry = useCallback(() => {
    if (retrySession) retrySession();
  }, [retrySession]);

  const renderContent = () => {
    // Show loading state while determining initial view
    if (builderState === null) {
      return (
        <div className="builder-loading-screen">
          <div className="builder-loading-spinner" />
        </div>
      );
    }

    switch (builderState) {
      case BUILDER_STATES.BOOTING:
        return (
          <SessionBootScreen
            sessionStatus={sessionStatus}
            error={localError || sessionError}
            onRetry={() => {
              setLocalError(null);
              handleBootRetry();
            }}
            pendingAction={pendingAction}
          />
        );
      case BUILDER_STATES.PROJECTS:
        return (
          <BuilderProjectsGrid
            onSelectProject={async (project) => {
              const projectId = project.aws_project_id || project.id;

              if (isCloudMode && !sessionReady) {
                setPendingAction({ type: 'open', projectId });
                setBuilderState(BUILDER_STATES.BOOTING);
                return;
              }

              await setCurrentProject(project.id);
              setSelectedExperience({
                id: projectId,
                name: project.display_name || project.name,
                projectId: projectId,
              });
              setCurrentPrompt('');
              setBuilderState(BUILDER_STATES.WORKSPACE);
              navigate(`/experience/${projectId}`, { replace: true });
            }}
            onCreateNew={handleNewChat}
          />
        );
      case BUILDER_STATES.START:
        return (
          <BuilderStartScreen
            onSelectExperience={handleSelectExperience}
            onSelectTemplate={handleSelectTemplate}
          />
        );
      case BUILDER_STATES.PROMPT:
        return (
          <BuilderPromptScreen
            experience={selectedExperience}
            onSubmitPrompt={handleSubmitPrompt}
            onBack={handleBackToProjects}
          />
        );
      case BUILDER_STATES.WORKSPACE:
        return (
          <BuilderWorkspace
            experience={selectedExperience}
            initialPrompt={currentPrompt}
            onNewChat={handleNewChat}
            chatSidebarOpen={chatSidebarOpen}
            viewMode={viewMode}
            deviceMode={deviceMode}
          />
        );
      default:
        return null;
    }
  };

  // Determine if we should show the sidebar
  const showSidebar = builderState !== null &&
                      builderState !== BUILDER_STATES.WORKSPACE &&
                      builderState !== BUILDER_STATES.BOOTING;

  return (
    <div className={`builder-page ${builderState === 'workspace' ? 'workspace-mode' : 'content-mode'}`}>
      {showSidebar && (
        <BuilderSidebar
          onNewChat={handleNewChat}
          onBackToProjects={projects.length > 0 ? handleBackToProjects : null}
        />
      )}
      <div className="builder-main">
        {renderContent()}
      </div>
    </div>
  );
}

export default BuilderPage;
